// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function AgentGroups() {
  const pageTitle = "Agent Groups";
  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [openNewCheckDialog, setOpenNewCheckDialog] = useState(false);

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [agentGroups, setAgentGroups] = useState([]);
  const [title, setTitle] = useState(null);

  async function deleteGroup(reference) {
    setShowLoading(true);

    await axios
      .get(`portal/agents/groups/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const groupColumns = [
    { Header: "#", accessor: "action", align: "left" },
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Join Link", accessor: "link", align: "left" },
    // { Header: "Added On", accessor: "created", align: "left" },
  ];

  const buildRateAgentGroupRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        name: row.agent_group.title,
        link: (
          <MDBox mt={1} variant="button">
            <a href={row.agent_group.join_link} target="_blank" rel="noreferrer">
              <MDTypography variant="caption" fontWeight="light">
                {row.agent_group.join_link}
              </MDTypography>
            </a>
          </MDBox>
        ),
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="Insights">
              <MDButton
                size="small"
                variant="outlined"
                color="warning"
                iconOnly
                onClick={() => {
                  navigate(`/agent-groups/insights/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  bar_chart
                </Icon>
              </MDButton>
            </Tooltip>
            &nbsp;
            <Tooltip title="Manage Group">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/agent-groups/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  settings
                </Icon>
              </MDButton>
            </Tooltip>
            &nbsp;
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Delete Group ${row.agent_group.title}?`) === true) {
                  deleteGroup(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        // created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/agents/groups.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRateAgentGroupRows(result.data.data.agent_groups);
            setAgentGroups(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function createAgentGroup() {
    if (title === null) {
      setSeverity("error");
      setErrMsg("Group name is required.");
      setOpen(true);
      return;
    }

    setOpenNewCheckDialog(false);
    setShowLoading(true);
    const json = {
      title: `${title}`,
    };

    await axios
      .post("portal/agents/groups/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Agent Group created!");
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewCheckDialog}>
        <DialogTitle>
          <MDBox>New Group</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mb={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setTitle(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewCheckDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              createAgentGroup();
            }}
          >
            Add Group
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={0}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>groups</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                        Organise your teams efficiently with groups. Assign team leads, commissions
                        and targets.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={5} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          setOpenNewCheckDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Group
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            {agentGroups.length > 0 && (
              <MDBox mt={5}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={0}>
                    <MDBox
                      pt={2}
                      px={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" fontWeight="medium">
                        Agent Groups
                      </MDTypography>
                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setOpenNewCheckDialog(true);
                          }}
                          iconOnly
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox mt={5}>
                      <DataTable
                        table={{ columns: groupColumns, rows: agentGroups }}
                        isSorted
                        entriesPerPage
                        showTotalEntries
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AgentGroups;
